import {formatMoney} from '@shopify/theme-currency';
import {getUrlWithVariant, ProductForm} from '@shopify/theme-product-form';
import {register} from '@shopify/theme-sections';
import Flickity from 'flickity';
import Drift from 'drift-zoom';

import 'flickity-imagesloaded';
import qtySelector from '../components/qty-selector';
import initSizeGuideModal from '../components/size-guide';
import modelViewerLoader from '../components/model-viewer-loader';
import {playPause} from '../utils/play-pause';
import initRecipientFields from '../components/recipient-fields';
import ModalDialog from '../components/modal-dialog';
import {initKlaviyoNotifyMe} from '../components/klaviyo-notify-me';

const selectors = {
  addToCart: '[data-add-to-cart]',
  comparePrice: '[data-product-compare-at-price]',
  priceWrapper: '[data-price-wrapper]',
  productForm: '[data-product-form]',
  modelsJson: '[data-product-models-json]',
  productPrice: '[data-product-price]',
  decrementQtyBtn: '[data-decrement-qty]',
  incrementQtyBtn: '[data-increment-qty]',
  qtyInput: '[data-qty-input]',
  sizeGuideMain: '[data-size-guide-main]',
  paymentModalTrigger: '[data-modal-payment-trigger]',
  paymentModal: '[data-modal-payment]',
  productAccordions: '.product__accordion',
  productDescriptionTrigger: '#accordion-trigger-product-description',
  productDescriptionPanel: '#accordion-panel-product-description',
  shippingModalTrigger: '[data-modal-shipping-trigger]',
  shippingModal: '[data-modal-shipping]',
  refundsModalTrigger: '[data-modal-refunds-trigger]',
  refundsModal: '[data-modal-refunds]',
  productInventory: '[data-product-inventory]',
  shopTheStory: '[data-shop-the-story-slider]',
  unisexDisclaimer: '.product__unisex-disclaimer',
  notifyMeForm: '[data-klaviyo-notify-form]',
  notifyMeBtn: '[data-notify-me-btn]',
  // Product Gallery
  productSlideshow: '.product__slideshow',
  productMediaCounter: '[data-product-media-counter]',
  productMediaWrapper: '[data-media-wrapper]',
  productImageWrapper: '[data-product-image-wrapper]',
  productVideoWrapper: '[data-product-video-wrapper]',
};

const classes = {
  hide: 'hide',
  onSale: 'product__price--on-sale',
  disabled: 'disabled',
  fadeIn: 'product__inventory-warning--fade-in',
};

const attributes = {
  disableAdd: 'data-disable-add',
  preorder: 'data-preorder',
  preorderMessage: 'data-preorder-message',
};

const sectionType = 'main-product';
const registeredSections = window.Shopify?.theme?.sections?.registered || {};
const isUnregistered = typeof registeredSections[sectionType] === 'undefined';

export default isUnregistered &&
  register(sectionType, {
    onLoad() {
      // Register the product form using @shopify/theme-product-form
      const formElement = this.container.querySelector(selectors.productForm);
      const productJSON = window.productJSON;
      const options = {
        onOptionChange: this.onOptionChange.bind(this),
        onFormSubmit: this.onFormSubmit.bind(this),
        // theme-product-form requires callbacks to be passed
        // for all four form events.
        onQuantityChange: () => true,
        onPropertyChange: () => true,
      };
      if (formElement) {
        this.productForm = new ProductForm(formElement, productJSON, options);
      }

      // Parse the settings string passed through from liquid
      const settingsData = this.container.dataset.sectionSettings || '{}';
      this.sectionSettings = JSON.parse(settingsData);

      // If URL contains ?variant= param, get the selected variant ID
      const selectedVariant = this.container.dataset.selectedVariant;
      const selectedVariantAvailable = this.container.dataset.selectedVariantAvailable;

      // Initialise the recipient fields
      initRecipientFields();

      // Look for a product image slideshow
      this.slideshow = this.container.querySelector(selectors.productSlideshow);
      this.productZooms = [];
      this.pinchZooms = {};

      this.unisexDisclaimer = this.container.querySelector(selectors.unisexDisclaimer);

      // Set media counter on tablet and desktop
      this.slideshowCounter = this.container.querySelector(selectors.productMediaCounter);
      this.tabletQuery = window.matchMedia(`(min-width: ${window.theme.breakpoints.md}px)`);
      // Store in const for use inside flickity instance
      const tabletQuery = this.tabletQuery;

      this.mediaPositionObserver();

      if (selectedVariant) {
        const status = selectedVariantAvailable === 'false';

        const currentVariant = window.productJSON.variants.find((variant) => variant.id === Number(selectedVariant));

        this.updateNofityMeForm(selectedVariant, status);
        initKlaviyoNotifyMe(this.container, currentVariant);
      }

      // Initialise the slider
      this.slider = null;
      if (this.slideshow) {
        const container = this.container;
        const slideshow = this.slideshow;
        const slideshowCounter = this.slideshowCounter;
        const settings = this.sectionSettings;
        const updateFeaturedImage = this.updateFeaturedImage.bind(this);
        this.slider = new Flickity(slideshow, {
          draggable: '>1',
          prevNextButtons: false,
          pageDots: false,
          watchCSS: true,
          on: {
            ready() {
              const modelViewers = this.slider.querySelectorAll('model-viewer');
              if (modelViewers.length > 0) {
                const modelViewerVersion = modelViewers[0].dataset.version;
                const modelsData = container.querySelector(selectors.modelsJson)?.textContent;
                if (modelViewerVersion && modelsData) {
                  const modelsDataJson = JSON.parse(modelsData);
                  modelViewerLoader(modelViewerVersion, modelsDataJson)
                    .then(() => null)
                    .catch(() => null);
                }

                // Stop slider drag events from impacting 3D model interaction
                const modelStopPropagation = (event) => event.stopPropagation();
                modelViewers.forEach((modelViewer) => {
                  modelViewer.addEventListener('mousedown', modelStopPropagation);
                  modelViewer.addEventListener('touchstart', modelStopPropagation);
                  modelViewer.addEventListener('pointerdown', modelStopPropagation);
                });
              }

              // Select the slider image matching the current variant
              updateFeaturedImage(selectedVariant ? selectedVariant : null, this);
            },

            change(index) {
              // Update counter
              if (slideshowCounter) {
                slideshowCounter.textContent = `0${index + 1}`.slice(-2);
              }

              // Pause or play videos on slide change depending on settings
              const videoMediaWrappers = this.slider.querySelectorAll(selectors.productVideoWrapper);
              videoMediaWrappers.forEach((wrapper) => {
                // See if current video is the current slide
                const slideIndex = parseInt(wrapper.dataset.slideIndex, 10);

                // Internal videos
                if (wrapper.dataset.mediaType === 'video') {
                  const video = wrapper.querySelector('video');

                  // Play current video if autoplay enabled
                  if (index === slideIndex && settings.video_controls === 'autoplay') {
                    video.play();
                  } else {
                    video.pause();
                  }

                  // External videos (YouTube/Vimeo)
                } else {
                  const iframe = wrapper.querySelector('iframe');

                  // Play current video if autoplay enabled
                  if (index === slideIndex && settings.video_controls === 'autoplay') {
                    playPause(iframe, 'play');
                  } else {
                    playPause(iframe, 'pause');
                  }
                }
              });

              // Reset zoom on all slider images if not tablet/desktop
              if (!tabletQuery.matches) {
                const sliderImages = this.slider.querySelectorAll(selectors.productImageWrapper);
                sliderImages.forEach((wrapper) => {
                  const image = wrapper.querySelector('img');
                  if (image) {
                    image.style.transform = '';
                    image.style.WebkitTransform = '';
                    image.style.zIndex = '';
                    image.classList.remove('pinch-zoom-active');
                  }
                });
              }
            },
          },
        });

        // Initialize zoom based on device
        if (this.tabletQuery.matches) {
          this.initDesktopZoom();
        } else {
          this.initMobileZoom();
        }

        // On media query change
        this.tabletQuery.addEventListener('change', (event) => {
          if (event.matches) {
            // Desktop: destroy any existing zooms and init desktop zoom
            this.productZooms.forEach((zoom) => zoom.destroy());
            this.productZooms = [];
            this.initDesktopZoom();
          } else {
            // Mobile: destroy desktop zooms and init mobile zoom
            this.productZooms.forEach((zoom) => zoom.destroy());
            this.productZooms = [];
            this.initMobileZoom();
          }
        });
      }

      const container = this.container;
      const shopStorySlider = container.querySelectorAll(selectors.shopTheStory);
      if (shopStorySlider.length > 1) {
        this.shopStorySlider = new Flickity(shopStorySlider[1], {
          freeScroll: false,
          prevNextButtons: false,
          pageDots: true,
          imagesLoaded: true,
          setGallerySize: true,
          cellAlign: 'left',
          groupCells: true,
          watchCSS: true,
          contain: true,
        });
      }

      qtySelector(
        this.container.querySelector(selectors.decrementQtyBtn),
        this.container.querySelector(selectors.incrementQtyBtn),
        this.container.querySelector(selectors.qtyInput)
      );

      initSizeGuideModal(this.container);

      // Initialise payment modal
      const paymentModalTrigger = this.container.querySelectorAll(selectors.paymentModalTrigger);
      const paymentModalElem = this.container.querySelector(selectors.paymentModal);

      if (paymentModalTrigger.length > 0 && paymentModalElem) {
        this.paymentModal = new ModalDialog(paymentModalElem, {
          triggerEl: paymentModalTrigger,
          modalId: 'payment',
        });
      }

      // Initialise shipping modal
      const shippingModalTrigger = this.container.querySelectorAll(selectors.shippingModalTrigger);
      const shippingModalElem = this.container.querySelector(selectors.shippingModal);

      if (shippingModalTrigger.length > 0 && shippingModalElem) {
        // eslint-disable-next-line no-unused-vars
        const shippingModal = new ModalDialog(shippingModalElem, {
          triggerEl: shippingModalTrigger,
          modalId: 'shipping',
        });
      }

      // Initialise refunds modal
      const refundsModalTrigger = this.container.querySelectorAll(selectors.refundsModalTrigger);
      const refundsModalElem = this.container.querySelector(selectors.refundsModal);

      if (refundsModalTrigger.length > 0 && refundsModalElem) {
        this.refundsModal = new ModalDialog(refundsModalElem, {
          triggerEl: refundsModalTrigger,
          modalId: 'refunds',
        });
      }

      // Product description accordion - auto close after some time
      const accordionsContainer = this.container.querySelector(selectors.productAccordions);
      let autoCloseTimeout;

      const autoCloseAccordion = () => {
        const trigger = this.container.querySelector(selectors.productDescriptionTrigger);
        const panel = this.container.querySelector(selectors.productDescriptionPanel);

        if (!trigger || !panel) return;

        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

        if (!isExpanded) return;

        autoCloseTimeout = setTimeout(() => {
          trigger.setAttribute('aria-expanded', false);
          panel.style.height = 0;
        }, 4000);
      };

      accordionsContainer?.addEventListener('mouseleave', autoCloseAccordion);

      accordionsContainer?.addEventListener('mouseenter', () => {
        if (!autoCloseTimeout) return;
        clearTimeout(autoCloseTimeout);
      });
    },

    // Event callback for option/variant switch event
    onOptionChange(event) {
      const variant = event.dataset.variant;

      /* eslint-disable no-negated-condition */
      if (!variant) {
        // The combination of selected options does not have a matching variant
        this.updateAddToCartState(null);
        this.updateProductPrices(false);
        this.updateFeaturedImage(false);
        this.updateInventoryStatus(false);
        this.updateNofityMeForm(null, false);
        initKlaviyoNotifyMe(this.container, null, false);
      } else if (!variant.available) {
        // The combination of selected options has a matching variant but it is
        // currently unavailable
        this.updateAddToCartState(false);
        this.updateProductPrices(true, variant.price, variant.compare_at_price);
        this.updateFeaturedImage(variant.id);
        this.updateInventoryStatus(variant.id);
        this.updateNofityMeForm(variant.id, true);
        initKlaviyoNotifyMe(this.container, variant, false);

        // Notify components about variant / size change
        this.notifyCustomElements(variant);

        const url = getUrlWithVariant(window.location.href, variant.id);
        window.history.replaceState({path: url}, '', url);
      } else {
        // The combination of selected options has a matching variant and it is
        // available
        this.updateAddToCartState(true);
        this.updateProductPrices(true, variant.price, variant.compare_at_price);
        this.updateFeaturedImage(variant.id);
        this.updateInventoryStatus(variant.id);
        this.updateNofityMeForm(variant.id, false);
        initKlaviyoNotifyMe(this.container, variant, true);

        // Notify components about variant / size change
        this.notifyCustomElements(variant);

        const url = getUrlWithVariant(window.location.href, variant.id);
        window.history.replaceState({path: url}, '', url);
      }

      if (!this.unisexDisclaimer) {
        return;
      }

      const content = this.unisexDisclaimer.querySelector('p');
      this.unisexDisclaimer.style.height = `calc(${content.scrollHeight}px + 32px)`;
      this.unisexDisclaimer.style.overflowY = 'unset';
      this.unisexDisclaimer.classList.add('open');

      /* eslint-enable no-negated-condition */
    },

    // Event callback for Theme Editor `section:unload` event
    onUnload() {
      this.productForm.destroy();
      this.shopStorySlider?.destroy();
      this.paymentModal?.destroy();
      this.refundsModal?.destroy();
    },

    validateProperties(data) {
      const properties = data || {};

      // Filter properties with empty values
      Object.keys(properties).forEach((key) => {
        if (properties[key] !== '') return;
        delete properties[key];
      });

      // Gift Card recipient fields validation
      const recipientFields = this.container.querySelector('recipient-fields');
      if (recipientFields) {
        const requiredFieldsSet = recipientFields.fields.every((field) =>
          field.required ? properties[field.name] : true
        );
        if (!requiredFieldsSet) {
          recipientFields.fields.forEach((field) => delete properties[field.name]);
        }
      }

      return properties;
    },

    // Event callback for product form submit event
    onFormSubmit(event) {
      event.preventDefault();
      // Show select size error if no size chosen
      const addToCartBtn = this.container.querySelector(selectors.addToCart);
      if (addToCartBtn?.hasAttribute(attributes.disableAdd)) {
        addToCartBtn.classList.add(classes.disabled);
        addToCartBtn.textContent = theme.strings.selectSize;
        return;
      }

      const variant = event.dataset.variant;
      const quantity = event.dataset.quantity;
      let preorder = false;
      let preorderMessage = '';

      if (addToCartBtn?.hasAttribute(attributes.preorder) && addToCartBtn?.hasAttribute(attributes.preorderMessage)) {
        preorder = true;
        preorderMessage = addToCartBtn.getAttribute(attributes.preorderMessage);
      }
      const properties = this.validateProperties(event.dataset.properties);

      const compareAtPrice = variant.compare_at_price;

      if (variant && variant.id && quantity) {
        const item = {
          id: variant.id,
          quantity,
          properties: {
            ...properties,
            ...(compareAtPrice && {_compare_at_price: compareAtPrice}),
            ...(preorder && {preorder: preorderMessage}),
          },
        };

        // Remove the properties object if it's empty
        if (Object.keys(item.properties).length === 0) {
          delete item.properties;
        }
        window.sideCart.handleAddToCartEvent(item, true);
      }
    },

    /**
     * Updates the DOM state of the add to cart button
     *
     * @param {boolean} enabledState - True is enabled. False is disabled. Null is disabled without prices.
     * @param {string} priceWrapper - DOM selector for prices container div
     * @param {string} button - DOM selector for add to cart button
     */
    updateAddToCartState(enabledState) {
      const addToCartBtn = this.container.querySelector(selectors.addToCart);
      const priceWrappers = this.container.querySelectorAll(selectors.priceWrapper);

      // Remove select size disabled state
      addToCartBtn.removeAttribute(attributes.disableAdd);
      addToCartBtn.classList.remove(classes.disabled);

      switch (enabledState) {
        // Variant is available
        case true: {
          priceWrappers.forEach((wrapper) => {
            wrapper.classList.remove(classes.hide);
          });
          addToCartBtn.removeAttribute('disabled');
          const preorder = addToCartBtn.hasAttribute(attributes.preorder);

          if (preorder) {
            addToCartBtn.textContent = theme.strings.preorder;
          } else {
            addToCartBtn.textContent = theme.strings.addToCart;
          }

          break;
        }
        // Variant is unavailable
        case false: {
          priceWrappers.forEach((wrapper) => {
            wrapper.classList.remove(classes.hide);
          });
          addToCartBtn.setAttribute('disabled', '');
          addToCartBtn.textContent = theme.strings.soldOut;
          break;
        }
        // No matching variant
        default: {
          priceWrappers.forEach((wrapper) => {
            wrapper.classList.add(classes.hide);
          });
          addToCartBtn.setAttribute('disabled', '');
          addToCartBtn.textContent = theme.strings.unavailable;
          break;
        }
      }
    },

    updateNofityMeForm(variantId, status) {
      const notifyMeBtn = document.querySelector(selectors.notifyMeBtn);
      if (!notifyMeBtn) return;

      if (status) {
        notifyMeBtn.classList.remove(classes.hide);
      } else {
        notifyMeBtn.classList.add(classes.hide);
      }
      // Klaviyo Form Variant Id update
      const notifyForm = document.querySelector(selectors.notifyMeForm);
      if (!notifyForm) return;

      const notifyFormElements = notifyForm.elements;
      const formVariantId = Number(notifyFormElements.variant.value);
      const selectedVariantId = Number(variantId);
      if (formVariantId === selectedVariantId) return;
      notifyFormElements.variant.value = selectedVariantId;
    },

    /**
     * when product option changed
     * fetch the corresponding inventory data and render it to pdp
     * @param {string} variantId - the current product variant id
     */
    updateInventoryStatus(variantId) {
      if (variantId) {
        fetch(`${this.productForm.product.url}?variant=${variantId}`, {cache: 'default'})
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const inventorySource = html.querySelector(selectors.productInventory);
            const inventoryDestination = document.querySelector(selectors.productInventory);
            if (inventorySource && inventoryDestination) {
              if (inventoryDestination.innerHTML === '' && inventorySource.innerHTML !== '') {
                inventoryDestination.classList.add(classes.fadeIn);
              } else {
                inventoryDestination.classList.remove(classes.fadeIn);
              }
              inventoryDestination.innerHTML = inventorySource.innerHTML;
            }
          })
          .catch((err) => {
            window.console.error('err', err);
          });
      }
    },

    /**
     * Updates the DOM with specified prices
     *
     * @param {boolean} variantExists - Selected options match a valid variant
     * @param {number} price - Selected variant price
     * @param {number} compareAtPrice - Selected variant original price
     * @param {string} productPrice - DOM selector for current price of the product
     * @param {string} comparePrice - DOM selector for original price of the product
     * @param {string} priceWrapper - DOM selector for prices container div
     */
    updateProductPrices(variantExists, price, compareAtPrice) {
      const comparePrices = this.container.querySelectorAll(selectors.comparePrice);
      const productPrices = this.container.querySelectorAll(selectors.productPrice);
      const priceWrappers = this.container.querySelectorAll(selectors.priceWrapper);

      // No matching variant
      if (!variantExists) {
        comparePrices.forEach((comparePrice) => {
          comparePrice.textContent = '';
          comparePrice.classList.add(classes.hide);
        });

        priceWrappers.forEach((wrapper) => {
          wrapper.classList.remove(classes.onSale);
        });
        return;
      }

      // Variant exists
      productPrices.forEach((productPrice) => {
        productPrice.textContent = formatMoney(price, theme.moneyWithCurrencyFormat);
      });

      if (price < compareAtPrice) {
        comparePrices.forEach((comparePrice) => {
          comparePrice.textContent = formatMoney(compareAtPrice, theme.moneyWithCurrencyFormat);
          comparePrice.classList.remove(classes.hide);
        });

        priceWrappers.forEach((wrapper) => {
          wrapper.classList.add(classes.onSale);
        });
      } else {
        comparePrices.forEach((comparePrice) => {
          comparePrice.textContent = '';
          comparePrice.classList.add(classes.hide);
        });

        priceWrappers.forEach((wrapper) => {
          wrapper.classList.remove(classes.onSale);
        });
      }
    },

    /**
     * Scrolls the image slider to that of the current variant
     *
     * @param {number} variantId - ID of the selected variant
     * @param {string} sliderImages - DOM selector for product image slider
     */
    updateFeaturedImage(variantId, thisSlider) {
      const id = variantId ? variantId : this.container.dataset.firstVariant;
      const slider = thisSlider ? thisSlider : this.slider;

      const sliderImages = this.container.querySelectorAll(selectors.productImageWrapper);
      if (sliderImages.length > 0) {
        for (let i = 0; i < sliderImages.length; i++) {
          const slide = sliderImages[i];
          const isVariantImage = slide.dataset.imageVariants.includes(id);

          if (isVariantImage && slider) {
            slider.select(Number(slide.dataset.slideIndex));
            break;
          }
        }
      }
    },

    initMobileZoom() {
      const sliderImages = this.container.querySelectorAll(selectors.productImageWrapper);

      if (!sliderImages.length) return;

      sliderImages.forEach((wrapper) => {
        const image = wrapper.querySelector('img');
        const container = image;
        const containerHeight = container.offsetHeight;
        const containerWidth = container.offsetWidth;
        const elementHeight = container.offsetHeight;
        const elementWidth = container.offsetWidth;

        let isZooming = false;
        const start = {};
        const maxScale = 4;
        const initial = {
          x: 0,
          y: 0,
          scale: 1,
        };
        const liveValues = {};

        // Calculate distance between two fingers
        const distance = (event) =>
          Math.hypot(event.touches[0].pageX - event.touches[1].pageX, event.touches[0].pageY - event.touches[1].pageY);

        const transformElement = (deltaX, deltaY, scale) => {
          const transform = `translate3d(${deltaX}px, ${deltaY}px, 0) scale(${scale})`;
          container.style.transform = transform;
          container.style.WebkitTransform = transform;
          container.style.zIndex = '9999';
        };

        let latestTap;
        const doubleTapZoom = (event) => {
          const now = new Date().getTime();
          const timesince = now - latestTap;
          if (timesince < 300 && timesince > 0) {
            const centerXOfImage = elementWidth * 2;
            const centerYOfImage = elementHeight * 2;
            const xZoomPosition = centerXOfImage - maxScale * event.pageX;
            const yZoomPosition = centerYOfImage - maxScale * event.pageY;
            initial.x = initial.x === 0 ? xZoomPosition : 0;
            initial.y = initial.y === 0 ? yZoomPosition : 0;
            initial.scale = initial.scale > 1 ? 1 : maxScale;
            container.classList.remove('pinch-zoom-active');
            transformElement(initial.x, initial.y, initial.scale);
          }

          latestTap = new Date().getTime();
        };

        image.addEventListener('touchstart', (event) => {
          if (event.touches.length !== 2) return;
          // Prevent page scroll
          event.preventDefault();
          container.classList.add('pinch-zoom-active');
          this.slider.options.draggable = false;
          this.slider.updateDraggable();
          isZooming = true;

          // Calculate where the fingers have started on the X and Y axis
          start.x = (event.touches[0].pageX + event.touches[1].pageX) / 2;
          start.y = (event.touches[0].pageY + event.touches[1].pageY) / 2;
          start.distance = distance(event);
          transformElement(initial.x, initial.y, initial.scale);
        });

        image.addEventListener('touchmove', (event) => {
          if (event.touches.length !== 2) return;
          // Prevent page scroll
          event.preventDefault();
          let scale;
          // Safari provides event.scale as two fingers move on the screen
          // For other browsers just calculate the scale manually
          if (event.scale) {
            scale = event.scale;
          } else {
            const deltaDistance = distance(event);
            scale = deltaDistance / start.distance;
          }

          const newScale = Math.min(maxScale, Math.max(1, initial.scale * scale));

          // Calculate how much the fingers have moved on the X and Y axis
          // x2 for accelerated movement
          let deltaX = newScale === 1 ? 0 : ((event.touches[0].pageX + event.touches[1].pageX) / 2 - start.x) * 2;
          let deltaY = newScale === 1 ? 0 : ((event.touches[0].pageY + event.touches[1].pageY) / 2 - start.y) * 2;

          // Add in transform position from previous pan if any
          deltaX += initial.x;
          deltaY += initial.y;

          // Constrain edges to keep image within boundary
          const overlapX = Math.max(0, Math.round((elementWidth * newScale - containerWidth) / 2));
          const overlapY = Math.max(0, Math.round((elementHeight * newScale - containerHeight) / 2));
          deltaX = Math.max(-overlapX, Math.min(overlapX, deltaX));
          deltaY = Math.max(-overlapY, Math.min(overlapY, deltaY));

          // Transform the image to make it grow and move with fingers
          transformElement(deltaX, deltaY, newScale);
          liveValues.x = deltaX;
          liveValues.y = deltaY;
          liveValues.scale = newScale;
        });

        container.addEventListener('touchend', (event) => {
          // Timeout to prevent flickity snapping on pinch end
          setTimeout(() => {
            if (isZooming) {
              initial.scale = liveValues.scale;
              initial.x = liveValues.x;
              initial.y = liveValues.y;
              this.slider.options.draggable = true;
              this.slider.updateDraggable();
              isZooming = false;
            } else {
              doubleTapZoom(event);
            }
          }, 100);
        });
      });
    },

    initDesktopZoom() {
      const sliderImages = this.container.querySelectorAll(selectors.productImageWrapper);

      if (!sliderImages.length) return;

      // Check if zoom is enabled in theme settings
      if (!this.sectionSettings.enable_zoom) return;

      sliderImages.forEach((wrapper) => {
        const image = wrapper.querySelector('img');
        if (!image || !image.dataset.zoom) return;

        const imageZoom = new Drift(image, {
          paneContainer: image.parentNode,
          inlinePane: false,
          zoomFactor: 2,
        });

        this.productZooms.push(imageZoom);
      });
    },

    /**
     * Observes the current media item and updates the counter
     */
    mediaPositionObserver() {
      const sliderMedia = this.container.querySelectorAll(selectors.productMediaWrapper);
      const videoMediaWrappers = this.container.querySelectorAll(selectors.productVideoWrapper);

      // Intersection observer callback for media items
      // Keeps track of current media item
      const observerCallback = (entries) => {
        if (!entries[0].isIntersecting) return;
        const target = entries[0].target;

        // Update counter
        const targetIndex = parseInt(target.dataset.slideIndex, 10);

        if (this.slideshowCounter) {
          this.slideshowCounter.textContent = `0${targetIndex + 1}`.slice(-2);
        }

        // Play video from visible slide, and pause other videos
        videoMediaWrappers.forEach((wrapper) => {
          // Internal videos
          if (wrapper.dataset.mediaType === 'video') {
            const video = wrapper.querySelector('video');

            // Play current video if autoplay enabled
            if (target.contains(video) && this.sectionSettings.video_controls === 'autoplay') {
              video.play();
            } else {
              video.pause();
            }

            // External videos (YouTube/Vimeo)
          } else {
            const iframe = wrapper.querySelector('iframe');

            // Play current video if autoplay enabled
            if (target.contains(iframe) && this.sectionSettings.video_controls === 'autoplay') {
              playPause(iframe, 'play');
            } else {
              playPause(iframe, 'pause');
            }
          }
        });
      };

      const mediaObserver = new IntersectionObserver(observerCallback, {threshold: 0.75});

      // Connect / disconnect observer when media query changes
      this.tabletQuery.addEventListener('change', (event) => {
        if (event.matches) {
          sliderMedia.forEach((media) => {
            mediaObserver.observe(media);
          });
          return;
        }

        mediaObserver.disconnect();
      });

      // Only observe on tablet and desktop
      if (!this.tabletQuery.matches) return;

      // Connect observer
      sliderMedia.forEach((media) => {
        mediaObserver.observe(media);
      });
    },

    /**
     * Notifies custom elements about variant changes
     * @param {Object} variant - The current variant object
     */
    notifyCustomElements(variant) {
      const customElements = ['product-measurements', 'measuring-guide'];

      customElements.forEach((customElement) => {
        const element = this.container?.querySelector(customElement);
        element?.dispatchEvent(
          new CustomEvent('variant:change', {
            detail: {variant},
          })
        );
      });
    },
  });
